import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { ShopierService } from '../../services/shopierService';
import { SiteData, ShopierTransaction, BillingOrder, BackgroundJob, ServiceHealthCheck } from '../../types';
import { JobService } from '../../services/jobService';
import { AdminServiceRequestsView, AdminPricingPlansView } from './AdminServicesAndPricing';
import { AdminPortfolioManager } from './AdminPortfolioManager';
import { AdminAIVisibilityManagement } from './AdminAIVisibilityManagement';
import { UserDetailModal } from './UserDetailModal';
import { AdminTenantsView } from './modules/AdminTenantsView';
import { AdminOrdersView } from './modules/AdminOrdersView';
import { AdminDomainsView } from './modules/AdminDomainsView';
import { AdminMessagesView } from './modules/AdminMessagesView';
import { AdminTrialsView } from './modules/AdminTrialsView';
import { AdminUpcomingPaymentsView } from './modules/AdminUpcomingPaymentsView';
import { AdminPlatformSettingsView } from './modules/AdminPlatformSettingsView';
import { AdminAuditLogsView } from './modules/AdminAuditLogsView';
import { formatDateTR, calculateRemainingDays, addMonthsSafely } from '../../utils/dateUtils';
import { adminApiService } from '../../services/adminApiService';
import { getSiteDisplayAddress } from '../../utils/security';
import {
  UserProfile,
} from '../../types';
import {
  Shield,
  ShieldCheck,
  Building2,
  Ship,
  Globe,
  CreditCard,
  HardDrive,
  Users,
  CheckCircle2,
  AlertCircle,
  PauseCircle,
  PlayCircle,
  Search,
  ExternalLink,
  DollarSign,
  Activity,
  Cpu,
  RefreshCw,
  Server,
  Zap,
  Briefcase,
  Tag,
  Clock,
  AlertTriangle,
  TrendingUp,
  Calendar,
  ArrowUpRight,
  Sparkles,
  ShoppingBag,
  Mail,
  Settings,
  FileText,
  Download,
} from 'lucide-react';
import { WebsiteExportModal } from '../export/WebsiteExportModal';

export const AdminDashboard: React.FC = () => {
  const { sites: initialSites, reloadSites, showToast } = useSite();
  const { users: initialUsers } = useAuth();

  // Real-time live synchronization states
  const [liveSites, setLiveSites] = useState<SiteData[]>([]);
  const [liveUsers, setLiveUsers] = useState<UserProfile[]>([]);
  const [liveTransactions, setLiveTransactions] = useState<ShopierTransaction[]>([]);
  const [liveOrders, setLiveOrders] = useState<BillingOrder[]>([]);
  const [liveJobs, setLiveJobs] = useState<BackgroundJob[]>([]);
  const [liveHealth, setLiveHealth] = useState<any>(null);
  const [loadingHealth, setLoadingHealth] = useState<boolean>(false);

  const [transactions, setTransactions] = useState<ShopierTransaction[]>([]);
  const [orders, setOrders] = useState<BillingOrder[]>([]);
  const [shopierHealth, setShopierHealth] = useState<any>(null);

  // Fallback to initial context if real-time listener hasn't received first emission yet
  const sites = liveSites.length > 0 ? liveSites : initialSites;
  const users = liveUsers.length > 0 ? liveUsers : initialUsers;
  const allOrders = liveOrders.length > 0 ? liveOrders : orders;
  const allTransactions = liveTransactions.length > 0 ? liveTransactions : transactions;
  type AdminTab =
    | 'sites'
    | 'pipeline'
    | 'users'
    | 'tenants'
    | 'payments'
    | 'orders'
    | 'upcoming'
    | 'ai-visibility'
    | 'domains'
    | 'messages'
    | 'trials'
    | 'observability'
    | 'settings'
    | 'logs'
    | 'jobs'
    | 'services'
    | 'pricing'
    | 'portfolio';

  const getInitialTab = (): AdminTab => {
    if (typeof window !== 'undefined') {
      const p = window.location.pathname.toLowerCase();
      if (p.includes('/admin/licenses') || p.includes('/admin/external-licenses')) return 'sites';
      if (p.includes('/admin/pipeline')) return 'pipeline';
      if (p.includes('/admin/tenants')) return 'tenants';
      if (p.includes('/admin/users')) return 'users';
      if (p.includes('/admin/orders')) return 'orders';
      if (p.includes('/admin/payments')) return 'payments';
      if (p.includes('/admin/upcoming')) return 'upcoming';
      if (p.includes('/admin/domains')) return 'domains';
      if (p.includes('/admin/messages')) return 'messages';
      if (p.includes('/admin/trials')) return 'trials';
      if (p.includes('/admin/settings')) return 'settings';
      if (p.includes('/admin/logs')) return 'logs';
      if (p.includes('/admin/observability')) return 'observability';
      if (p.includes('/admin/jobs')) return 'jobs';
      if (p.includes('/admin/services')) return 'services';
      if (p.includes('/admin/pricing')) return 'pricing';
      if (p.includes('/admin/portfolio')) return 'portfolio';
      if (p.includes('/admin/sites')) return 'sites';
    }
    return 'sites';
  };

  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<AdminTab>(getInitialTab);
  const [exportModalSite, setExportModalSite] = useState<SiteData | null>(null);

  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    if (typeof window !== 'undefined') {
      const target = tab === 'sites' ? '/admin' : `/admin/${tab}`;
      if (window.location.pathname !== target) {
        window.history.pushState({}, '', target);
      }
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setActiveTab(getInitialTab());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [isProcessingJobs, setIsProcessingJobs] = useState(false);
  const [selectedUserForDetail, setSelectedUserForDetail] = useState<UserProfile | null>(null);

  const fetchLiveHealth = async () => {
    try {
      setLoadingHealth(true);
      const res = await adminApiService.getSystemHealth();
      setLiveHealth(res);
    } catch (err) {
      console.warn('[Admin Health Check Error]:', err);
    } finally {
      setLoadingHealth(false);
    }
  };

  const loadBillingData = async () => {
    dbService.getPaymentTransactions().then((t) => {
      setTransactions(t);
      if (t.length > 0 && liveTransactions.length === 0) setLiveTransactions(t);
    });
    dbService.getAllOrders().then((o) => {
      setOrders(o);
      if (o.length > 0 && liveOrders.length === 0) setLiveOrders(o);
    });
    ShopierService.checkHealth().then(setShopierHealth);
  };

  useEffect(() => {
    loadBillingData();
    dbService.getAllBackgroundJobs().then(setJobs);
    fetchLiveHealth();

    // Attach real-time Firebase listeners for instant live updates across the whole admin suite
    const unsubSites = dbService.subscribeToSites((data) => setLiveSites(data));
    const unsubUsers = dbService.subscribeToUsers((data) => setLiveUsers(data));
    const unsubOrders = dbService.subscribeToOrders((data) => setLiveOrders(data));
    const unsubTxs = dbService.subscribeToTransactions((data) => setLiveTransactions(data));
    const unsubJobs = dbService.subscribeToBackgroundJobs((data) => setLiveJobs(data));

    const healthInterval = setInterval(fetchLiveHealth, 30000);

    return () => {
      unsubSites();
      unsubUsers();
      unsubOrders();
      unsubTxs();
      unsubJobs();
      clearInterval(healthInterval);
    };
  }, []);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const todayRevenue = allTransactions
    .filter((t) => t.status === 'SUCCESS' && t.createdAt >= startOfToday.getTime())
    .reduce((sum, t) => sum + t.amount, 0);

  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);
  const monthRevenue = allTransactions
    .filter((t) => t.status === 'SUCCESS' && t.createdAt >= startOfMonth.getTime())
    .reduce((sum, t) => sum + t.amount, 0);

  const activeUsersCount = users.filter((u) => u.status !== 'SUSPENDED').length;
  const activeSitesCount = sites.filter((s) => s.status === 'PUBLISHED').length;
  const trialUsersCount = users.filter((u) => u.trial?.isActive).length;
  const pendingOrdersCount = allOrders.filter((o) => o.status === 'PENDING').length;
  const currentJobsList = liveJobs.length > 0 ? liveJobs : jobs;

  const totalRevenue = allTransactions.reduce((sum, t) => sum + (t.status === 'SUCCESS' ? t.amount : 0), 0);
  const corporateSites = sites.filter((s) => s.industry === 'CORPORATE').length;
  const yachtSites = sites.filter((s) => s.industry === 'YACHT_RENTAL').length;

  // Real Database Renewal & Churn Pipeline Metrics
  const now = Date.now();
  const sitesWithSubEvaluation = sites.map((site) => {
    const expireDate = site.subscription?.expireDate || (site.createdAt ? site.createdAt + 365 * 86400000 : now + 365 * 86400000);
    const evalResult = ShopierService.evaluateSubscriptionStatus(expireDate);
    const annualPrice = site.industry === 'YACHT_RENTAL' ? 36000 : 18000;
    return {
      site,
      expireDate,
      evalResult,
      annualPrice,
    };
  });

  const activeSubscriptions = sitesWithSubEvaluation.filter((s) => s.evalResult.status === 'ACTIVE');
  const expiringSoonSubscriptions = sitesWithSubEvaluation.filter((s) => s.evalResult.status === 'EXPIRING_SOON');
  const expiredSubscriptions = sitesWithSubEvaluation.filter((s) => s.evalResult.status === 'EXPIRED');

  const projectedARR = sitesWithSubEvaluation.reduce((sum, s) => sum + s.annualPrice, 0);
  const renewalAtRiskRevenue = expiringSoonSubscriptions.reduce((sum, s) => sum + s.annualPrice, 0);
  const churnRate = sites.length > 0 ? Math.round((expiredSubscriptions.length / sites.length) * 100) : 0;

  const handleToggleSuspend = async (site: SiteData) => {
    const nextStatus = site.status === 'SUSPENDED' ? 'PUBLISHED' : 'SUSPENDED';
    await dbService.saveSite({ ...site, status: nextStatus as any });
    reloadSites();
  };

  const handleExtendSubscription = async (site: SiteData, monthsToAdd: number) => {
    try {
      const currentExpire = site.subscription?.expireDate && site.subscription.expireDate > now
        ? site.subscription.expireDate
        : now;
      const newExpireDate = currentExpire + monthsToAdd * 30 * 86400000;

      const updatedSite: SiteData = {
        ...site,
        subscription: {
          ...site.subscription,
          status: 'ACTIVE',
          planId: site.subscription?.planId || (site.industry === 'YACHT_RENTAL' ? 'PLAN_YACHT_ANNUAL' : 'PLAN_CORP_ANNUAL'),
          planName: site.subscription?.planName || (site.industry === 'YACHT_RENTAL' ? 'Yat Kiralama Yıllık' : 'Kurumsal Yıllık'),
          startDate: site.subscription?.startDate || now,
          expireDate: newExpireDate,
          websiteLimit: site.subscription?.websiteLimit || 1,
        },
      };

      await dbService.saveSite(updatedSite);
      reloadSites();
      if (showToast) {
        showToast(`Abonelik ${monthsToAdd} ay uzatıldı. Yeni bitiş: ${new Date(newExpireDate).toLocaleDateString('tr-TR')}`, 'success');
      }
    } catch (err) {
      console.error('Extend sub error:', err);
    }
  };

  const handleExtendDays = async (site: SiteData, daysToAdd: number) => {
    try {
      const currentExpire = site.subscription?.expireDate && site.subscription.expireDate > now
        ? site.subscription.expireDate
        : now;
      const newExpireDate = currentExpire + daysToAdd * 86400000;

      const updatedSite: SiteData = {
        ...site,
        subscription: {
          ...site.subscription,
          status: 'ACTIVE',
          planId: site.subscription?.planId || (site.industry === 'YACHT_RENTAL' ? 'PLAN_YACHT_ANNUAL' : 'PLAN_CORP_ANNUAL'),
          planName: site.subscription?.planName || (site.industry === 'YACHT_RENTAL' ? 'Yat Kiralama Yıllık' : 'Kurumsal Yıllık'),
          startDate: site.subscription?.startDate || now,
          expireDate: newExpireDate,
          websiteLimit: site.subscription?.websiteLimit || 1,
        },
      };

      await dbService.saveSite(updatedSite);
      reloadSites();
      if (showToast) {
        showToast(`Abonelik +${daysToAdd} gün uzatıldı. Yeni bitiş: ${formatDateTR(newExpireDate)}`, 'success');
      }
    } catch (err) {
      console.error('Extend days error:', err);
    }
  };

  const handleExpireSubscription = async (site: SiteData) => {
    try {
      const updatedSite: SiteData = {
        ...site,
        subscription: {
          ...site.subscription,
          status: 'EXPIRED',
          planId: site.subscription?.planId || 'PLAN_CORP_ANNUAL',
          planName: site.subscription?.planName || 'Kurumsal Yıllık',
          startDate: site.subscription?.startDate || now,
          expireDate: now - 86400000,
          websiteLimit: 1,
        },
      };
      await dbService.saveSite(updatedSite);
      reloadSites();
      if (showToast) {
        showToast(`Abonelik süresi doldu olarak işaretlendi.`, 'info');
      }
    } catch (err) {
      console.error('Expire sub error:', err);
    }
  };

  const handleTriggerJobs = async () => {
    setIsProcessingJobs(true);
    await JobService.processPendingJobs();
    const updated = await dbService.getAllBackgroundJobs();
    setJobs(updated);
    setIsProcessingJobs(false);
  };

  const filteredSites = sites.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.subdomain.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.customDomain && s.customDomain.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const healthMetrics: ServiceHealthCheck[] = [
    {
      service: 'DATABASE',
      status: 'HEALTHY',
      latencyMs: 38,
      message: 'Firebase Realtime DB bağlı ve kurallar doğrulanmış.',
      lastChecked: Date.now(),
    },
    {
      service: 'AUTH',
      status: 'HEALTHY',
      latencyMs: 42,
      message: 'Firebase Auth token doğrulama katmanı aktif.',
      lastChecked: Date.now(),
    },
    {
      service: 'STORAGE',
      status: 'HEALTHY',
      latencyMs: 65,
      message: 'Google Cloud Storage kova erişim kuralları devrede.',
      lastChecked: Date.now(),
    },
    {
      service: 'DOMAIN_RESOLVER',
      status: 'HEALTHY',
      latencyMs: 14,
      message: 'O(1) /domainMappings hızlı arama motoru devrede.',
      lastChecked: Date.now(),
    },
    {
      service: 'PAYMENT_GATEWAY',
      status: 'HEALTHY',
      latencyMs: 88,
      message: 'Shopier Gateway webhook dinleyicisi ve idempotency aktif.',
      lastChecked: Date.now(),
    },
    {
      service: 'PUBLIC_ENGINE',
      status: 'HEALTHY',
      latencyMs: 22,
      message: 'Snapshot izolasyonlu CDN render motoru hazır.',
      lastChecked: Date.now(),
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 sm:space-y-8 animate-in fade-in select-none w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-rose-500">
            <Shield className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Adiosoft SaaS Master Kontrol</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mt-1">
            Süper Yönetici (Admin) Paneli
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Platformdaki tüm müşteri hesaplarını, web sitelerini, domainleri, sistem sağlığını ve Shopier gelir akışını denetleyin.
          </p>
        </div>

        <div className="px-3.5 py-1.5 rounded-xl bg-rose-950/60 border border-rose-800/60 text-rose-300 text-xs font-bold flex items-center gap-1.5">
          <Shield className="w-4 h-4 text-rose-400" />
          Süper Admin Yetkisi Aktif
        </div>
      </div>

      {/* Primary KPI Grid - Real Live Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {/* Toplam & Aktif Kullanıcı */}
        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">Kullanıcılar</span>
            <Users className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div className="text-xl font-black text-white">{users.length}</div>
          <div className="text-[11px] text-emerald-400 font-semibold">{activeUsersCount} Aktif Hesap</div>
        </div>

        {/* Aktif Web Sitesi */}
        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">Web Siteleri</span>
            <Globe className="w-3.5 h-3.5 text-indigo-400" />
          </div>
          <div className="text-xl font-black text-white">{sites.length}</div>
          <div className="text-[11px] text-slate-400 font-medium">{activeSitesCount} Yayında</div>
        </div>

        {/* Aktif Abonelikler */}
        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">Abonelikler</span>
            <CreditCard className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <div className="text-xl font-black text-white">{activeSubscriptions.length}</div>
          <div className="text-[11px] text-amber-400 font-semibold">{expiringSoonSubscriptions.length} Bitişi Yakın</div>
        </div>

        {/* Trial & Bekleyen Sipariş */}
        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">Trial & Sipariş</span>
            <Clock className="w-3.5 h-3.5 text-purple-400" />
          </div>
          <div className="text-xl font-black text-white">{trialUsersCount} Trial</div>
          <div className="text-[11px] text-slate-400 font-medium">{pendingOrdersCount} Bekleyen Sipariş</div>
        </div>

        {/* Bugünkü & Aylık Gelir */}
        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">Shopier Gelir</span>
            <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <div className="text-lg font-black text-white">{monthRevenue.toLocaleString('tr-TR')} TL</div>
          <div className="text-[11px] text-emerald-400 font-semibold">Bugün: {todayRevenue.toLocaleString('tr-TR')} TL</div>
        </div>
      </div>

      {/* Navigation Tabs (Scrollable on mobile) */}
      <div className="flex items-center gap-2 border-b border-slate-800 pb-2 overflow-x-auto custom-scrollbar whitespace-nowrap">
        <button
          onClick={() => handleTabChange('sites')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'sites' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Globe className="w-4 h-4" />
          Siteler & Domainler ({sites.length})
        </button>

        <button
          onClick={() => handleTabChange('ai-visibility')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'ai-visibility' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Sparkles className="w-4 h-4 text-indigo-400" />
          AI Arama Görünürlüğü
        </button>

        <button
          onClick={() => handleTabChange('pipeline')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'pipeline' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          Satış & Yenileme Hattı ({expiringSoonSubscriptions.length + expiredSubscriptions.length})
        </button>

        <button
          onClick={() => handleTabChange('users')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'users' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Users className="w-4 h-4" />
          Müşteriler ({users.length})
        </button>

        <button
          onClick={() => handleTabChange('tenants')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'tenants' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Şirketler (Tenants)
        </button>

        <button
          onClick={() => handleTabChange('orders')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'orders' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <ShoppingBag className="w-4 h-4" />
          Siparişler ({allOrders.length})
        </button>

        <button
          onClick={() => handleTabChange('payments')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'payments' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          Ödemeler ({allTransactions.length})
        </button>

        <button
          onClick={() => handleTabChange('upcoming')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'upcoming' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Calendar className="w-4 h-4 text-emerald-400" />
          Yaklaşan Yenilemeler
        </button>

        <button
          onClick={() => handleTabChange('domains')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'domains' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Globe className="w-4 h-4 text-blue-400" />
          Domainler & SSL
        </button>

        <button
          onClick={() => handleTabChange('messages')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'messages' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Mail className="w-4 h-4 text-purple-400" />
          İletişim Mesajları
        </button>

        <button
          onClick={() => handleTabChange('trials')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'trials' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Clock className="w-4 h-4 text-amber-400" />
          Trial Hesapları
        </button>

        <button
          onClick={() => handleTabChange('observability')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'observability' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Activity className="w-4 h-4" />
          Sistem Sağlığı & İzleme
        </button>

        <button
          onClick={() => handleTabChange('settings')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'settings' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Settings className="w-4 h-4 text-slate-400" />
          Şirket Ayarları
        </button>

        <button
          onClick={() => handleTabChange('logs')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'logs' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <FileText className="w-4 h-4 text-rose-400" />
          Denetim Kayıtları
        </button>

        <button
          onClick={() => handleTabChange('jobs')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'jobs' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Cpu className="w-4 h-4" />
          Arka Plan Görevleri ({currentJobsList.length})
        </button>

        <button
          onClick={() => handleTabChange('services')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'services' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Briefcase className="w-4 h-4" />
          Hizmet Talepleri
        </button>

        <button
          onClick={() => handleTabChange('pricing')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'pricing' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Tag className="w-4 h-4" />
          Fiyatlandırma & Paketler
        </button>

        <button
          onClick={() => handleTabChange('portfolio')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 shrink-0 ${
            activeTab === 'portfolio' ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30' : 'text-slate-400 hover:text-white hover:bg-slate-850'
          }`}
        >
          <Sparkles className="w-4 h-4 text-amber-400" />
          Portfolyo & Referanslar
        </button>
      </div>

      {/* TAB: RENEWAL & SALES PIPELINE */}
      {activeTab === 'pipeline' && (
        <div className="space-y-6">
          {/* Revenue & Pipeline Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs">
              <div className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-wider">
                <span>Yıllık Gelir Potansiyeli (ARR)</span>
                <DollarSign className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-2xl font-black text-slate-900 mt-2">
                {projectedARR.toLocaleString('tr-TR')} TL
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {sites.length} aktif sitenin yıllık abonelik değeri
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs">
              <div className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-wider">
                <span>30 Günlük Yenileme Hattı</span>
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div className="text-2xl font-black text-amber-700 mt-2">
                {renewalAtRiskRevenue.toLocaleString('tr-TR')} TL
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {expiringSoonSubscriptions.length} sitenin aboneliği 30 gün içinde doluyor
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs">
              <div className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-wider">
                <span>Süresi Dolan / Churn Riski</span>
                <AlertTriangle className="w-4 h-4 text-rose-600" />
              </div>
              <div className="text-2xl font-black text-rose-700 mt-2">
                {expiredSubscriptions.length} Site (%{churnRate})
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Yenileme yapılmadığı takdirde askıya alınabilir
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs">
              <div className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-wider">
                <span>Aktif Sağlıklı Abonelik</span>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-2xl font-black text-emerald-700 mt-2">
                {activeSubscriptions.length} / {sites.length}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Düzenli yayın ve yenileme döngüsünde
              </p>
            </div>
          </div>

          {/* Pipeline Sites Table */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span>Abonelik Yenileme & Satış Takip Listesi</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Tüm müşteri sitelerinin kalan gün sayısı, yenileme potansiyeli ve süresi dolan hesapları denetleyin.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-semibold">
                  Toplam {sitesWithSubEvaluation.length} Kayıt
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3.5 px-4">Web Sitesi & Sahip</th>
                    <th className="py-3.5 px-4">Sektör & Paket</th>
                    <th className="py-3.5 px-4">Yenileme Bedeli</th>
                    <th className="py-3.5 px-4">Bitiş Tarihi</th>
                    <th className="py-3.5 px-4">Kalan Süre</th>
                    <th className="py-3.5 px-4">Durum</th>
                    <th className="py-3.5 px-4 text-right">Süper Admin Eylemleri</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sitesWithSubEvaluation.map(({ site, evalResult, annualPrice }) => {
                    const isExp = evalResult.status === 'EXPIRED';
                    const isSoon = evalResult.status === 'EXPIRING_SOON';
                    const isYacht = site.industry === 'YACHT_RENTAL';

                    return (
                      <tr key={site.id} className="hover:bg-slate-50/80 transition">
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-900 text-sm">{site.name}</div>
                          <div className="text-[11px] text-slate-400 font-mono">
                            {getSiteDisplayAddress(site)}
                          </div>
                        </td>

                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
                            {isYacht ? <Ship className="w-3.5 h-3.5 text-sky-600" /> : <Building2 className="w-3.5 h-3.5 text-blue-600" />}
                            {isYacht ? 'Yat Kiralama Portalı' : 'Kurumsal Web Sitesi'}
                          </span>
                        </td>

                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-900">
                            {annualPrice.toLocaleString('tr-TR')} TL / yıl
                          </div>
                          <div className="text-[10px] text-slate-400">1 Yıllık Lisans</div>
                        </td>

                        <td className="py-3.5 px-4 text-slate-600 font-medium">
                          {evalResult.formattedExpireDate}
                        </td>

                        <td className="py-3.5 px-4">
                          {isExp ? (
                            <span className="font-bold text-rose-600">Süresi Doldu</span>
                          ) : (
                            <span className="font-semibold text-slate-800">
                              {evalResult.daysRemaining} Gün Kaldı
                            </span>
                          )}
                        </td>

                        <td className="py-3.5 px-4">
                          {isExp ? (
                            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800">
                              Yenileme Bekliyor
                            </span>
                          ) : isSoon ? (
                            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                              Yaklaşıyor (&le;30 Gün)
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                              Aktif Abonelik
                            </span>
                          )}
                        </td>

                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleExtendSubscription(site, 12)}
                              className="px-2.5 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold text-[11px] transition flex items-center gap-1 cursor-pointer"
                              title="1 Yıl Manuel Uzat"
                            >
                              <RefreshCw className="w-3 h-3" />
                              <span>+1 Yıl</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleExtendSubscription(site, 1)}
                              className="px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-[11px] transition flex items-center gap-1 cursor-pointer"
                              title="1 Ay Opsiyon Ver"
                            >
                              <span>+1 Ay</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleExtendDays(site, 7)}
                              className="px-2 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-[11px] transition flex items-center gap-1 cursor-pointer"
                              title="7 Gün Ekstra Ver"
                            >
                              <span>+7 Gün</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleExpireSubscription(site)}
                              className="px-2 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 font-bold text-[11px] transition flex items-center gap-1 cursor-pointer"
                              title="Aboneliği Sonlandır / Süresi Doldu Yap"
                            >
                              <span>Süresi Doldu</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleToggleSuspend(site)}
                              className={`p-1.5 rounded-lg transition cursor-pointer ${
                                site.status === 'SUSPENDED'
                                  ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                                  : 'bg-rose-50 text-rose-600 hover:bg-rose-100'
                              }`}
                              title={site.status === 'SUSPENDED' ? 'Yayına Al' : 'Askıya Al'}
                            >
                              {site.status === 'SUSPENDED' ? (
                                <PlayCircle className="w-4 h-4" />
                              ) : (
                                <PauseCircle className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 1: SITES */}
      {activeTab === 'sites' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm max-w-md">
            <Search className="w-4 h-4 text-slate-400 ml-2" />
            <input
              type="text"
              placeholder="Site adı, subdomain veya özel domain ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-xs text-slate-900 placeholder:text-slate-400 font-medium outline-none"
            />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3.5 px-4">Site Adı</th>
                  <th className="py-3.5 px-4">Sektör</th>
                  <th className="py-3.5 px-4">Subdomain / Domain</th>
                  <th className="py-3.5 px-4">Yayın Durumu</th>
                  <th className="py-3.5 px-4">Abonelik</th>
                  <th className="py-3.5 px-4 text-right">Eylemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSites.map((site) => (
                  <tr key={site.id} className="hover:bg-slate-50">
                    <td className="py-3 px-4 font-bold text-slate-900">{site.name}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                        {site.industry === 'CORPORATE' ? 'Kurumsal' : 'Yat Kiralama'}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-[11px] text-slate-600">
                      <div>{getSiteDisplayAddress(site)}</div>
                      {site.customDomain && (
                        <div className="text-teal-600 font-bold flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {site.customDomain}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          site.status === 'PUBLISHED'
                            ? 'bg-emerald-100 text-emerald-800'
                            : site.status === 'SUSPENDED'
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {site.status === 'PUBLISHED' ? 'Yayında' : site.status === 'SUSPENDED' ? 'Askıda' : 'Taslak'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-[11px] font-semibold text-slate-700">
                        {site.subscription?.planName || 'Yıllık Paket'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setExportModalSite(site)}
                          className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold text-[10px] transition flex items-center gap-1 cursor-pointer"
                          title="HTML/CSS/JS İndir & Kaynak Kodu Gör"
                        >
                          <Download className="w-3 h-3" />
                          <span>HTML İndir</span>
                        </button>

                        <button
                          onClick={() => handleToggleSuspend(site)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition flex items-center gap-1 ${
                            site.status === 'SUSPENDED'
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                              : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                          }`}
                        >
                          {site.status === 'SUSPENDED' ? (
                            <>
                              <PlayCircle className="w-3 h-3" /> Yayına Al
                            </>
                          ) : (
                            <>
                              <PauseCircle className="w-3 h-3" /> Askıya Al
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: AI SEARCH VISIBILITY MANAGEMENT */}
      {activeTab === 'ai-visibility' && (
        <AdminAIVisibilityManagement sites={sites} users={users} />
      )}

      {/* TAB 2: USERS */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 bg-white p-2 rounded-2xl border border-slate-200 shadow-2xs max-w-md w-full">
              <Search className="w-4 h-4 text-slate-400 ml-2" />
              <input
                type="text"
                placeholder="Müşteri adı, e-posta veya şirket ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent text-xs text-slate-900 placeholder:text-slate-400 font-medium outline-none"
              />
            </div>
            <div className="text-xs font-semibold text-slate-500">
              Toplam {users.length} Kayıtlı Kullanıcı
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3.5 px-4">Müşteri</th>
                  <th className="py-3.5 px-4">Şirket</th>
                  <th className="py-3.5 px-4 text-center">Web Sitesi</th>
                  <th className="py-3.5 px-4">Site Aboneliği</th>
                  <th className="py-3.5 px-4">Kayıt Tarihi</th>
                  <th className="py-3.5 px-4">Hesap Durumu</th>
                  <th className="py-3.5 px-4 text-right">Eylemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users
                  .filter((u) => {
                    const q = searchQuery.toLowerCase();
                    return (
                      (u.displayName || '').toLowerCase().includes(q) ||
                      (u.email || '').toLowerCase().includes(q) ||
                      (u.companyName || '').toLowerCase().includes(q)
                    );
                  })
                  .map((u) => {
                    const userSites = sites.filter((s) => s.ownerId === u.uid || s.tenantId === u.uid);
                    const isSuspended = u.status === 'SUSPENDED';

                    // Determine site subscription badge
                    const activeSiteSub = userSites.some((s) => s.subscription?.status === 'ACTIVE');

                    return (
                      <tr key={u.uid} className="hover:bg-slate-50 transition">
                        <td className="py-3 px-4">
                          <div className="font-bold text-slate-900">{u.displayName || 'İsimsiz'}</div>
                          <div className="text-slate-500 font-mono text-[11px]">{u.email}</div>
                        </td>
                        <td className="py-3 px-4 font-semibold text-slate-700">{u.companyName || '—'}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700">
                            {userSites.length}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {activeSiteSub ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                              Aktif Abonelik
                            </span>
                          ) : u.trial?.isActive ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800">
                              Trial
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600">
                              Pasif / Yok
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-400 font-mono text-[11px]">
                          {formatDateTR(u.createdAt)}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              isSuspended
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {isSuspended ? 'Askıya Alındı' : 'Aktif'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => setSelectedUserForDetail(u)}
                              className="px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold text-[11px] transition flex items-center gap-1"
                              title="Paket / Abonelik Ata"
                            >
                              <CreditCard className="w-3 h-3" />
                              <span>Abonelik Ata</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setSelectedUserForDetail(u)}
                              className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-[11px] transition"
                            >
                              Detay Gör
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const nextStatus = isSuspended ? 'ACTIVE' : 'SUSPENDED';
                                await dbService.updateUserStatus(u.uid, nextStatus);
                                u.status = nextStatus;
                                if (showToast) {
                                  showToast(`Kullanıcı durumu: ${nextStatus === 'ACTIVE' ? 'Aktif' : 'Askıda'}`, 'info');
                                }
                              }}
                              className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition ${
                                isSuspended
                                  ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                  : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                              }`}
                            >
                              {isSuspended ? 'Aktif Et' : 'Askıya Al'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: PAYMENTS & SHOPIER GATEWAY */}
      {activeTab === 'payments' && (
        <div className="space-y-6">
          {/* Shopier Gateway Production Status */}
          <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 flex items-center justify-center font-black text-lg shadow-inner">
                S
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-slate-900">Shopier Ödeme Altyapısı</h3>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      shopierHealth?.configured
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {shopierHealth?.configured ? '● CANLI AKTİF (PAT Bağlı)' : '○ SHOPIER_PAT Bekleniyor'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Webhook Hedefi: <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono text-[11px] text-slate-700">POST /api/webhooks/shopier</code> (HMAC-SHA256 Doğrulamalı)
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={loadBillingData}
                className="px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold transition flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Yenile</span>
              </button>
            </div>
          </div>

          {/* Real Database Billing Orders */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-xs text-slate-800 uppercase tracking-wider">Dahili Siparişler & Shopier Eşleşmeleri ({orders.length})</h4>
                <p className="text-[11px] text-slate-500">Kullanıcıların başlattığı siparişler, durumları ve Shopier eşleşmeleri.</p>
              </div>
            </div>
            {orders.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">
                Henüz kayıtlı bir sipariş oturumu bulunmamaktadır.
              </div>
            ) : (
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3 px-4">Sipariş No</th>
                    <th className="py-3 px-4">Müşteri</th>
                    <th className="py-3 px-4">Paket</th>
                    <th className="py-3 px-4">Tutar</th>
                    <th className="py-3 px-4">Shopier ID</th>
                    <th className="py-3 px-4">Tarih</th>
                    <th className="py-3 px-4">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-mono font-bold text-slate-900">{order.id}</td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-800">{order.customerName}</div>
                        <div className="text-[10px] text-slate-400">{order.customerEmail}</div>
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-700">{order.planName}</td>
                      <td className="py-3 px-4 font-bold text-slate-900">
                        {order.amount.toLocaleString('tr-TR')} {order.currency}
                      </td>
                      <td className="py-3 px-4 font-mono text-[11px] text-slate-500">
                        {order.shopierOrderId || order.shopierProductId || '—'}
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-500 text-[11px]">
                        {new Date(order.createdAt).toLocaleDateString('tr-TR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            order.status === 'PAID'
                              ? 'bg-emerald-100 text-emerald-800'
                              : order.status === 'PENDING'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}
                        >
                          {order.status === 'PAID'
                            ? '✓ Ödendi'
                            : order.status === 'PENDING'
                            ? '⏳ Beklemede'
                            : '✕ Başarısız'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Past Transactions Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50/70">
              <h4 className="font-bold text-xs text-slate-800 uppercase tracking-wider">İşlem Geçmişi (Transactions)</h4>
            </div>
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3.5 px-4">İşlem ID</th>
                  <th className="py-3.5 px-4">Müşteri</th>
                  <th className="py-3.5 px-4">Paket Adı</th>
                  <th className="py-3.5 px-4">Tutar</th>
                  <th className="py-3.5 px-4">Tarih</th>
                  <th className="py-3.5 px-4">Durum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-slate-50">
                    <td className="py-3 px-4 font-mono font-bold text-slate-900">{tx.id}</td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-800">{tx.customerName}</div>
                      <div className="text-[10px] text-slate-400">{tx.customerEmail}</div>
                    </td>
                    <td className="py-3 px-4 font-semibold text-slate-700">{tx.planName}</td>
                    <td className="py-3 px-4 font-bold text-slate-900">
                      {tx.amount.toLocaleString('tr-TR')} {tx.currency}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-500 text-[11px]">
                      {new Date(tx.createdAt).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        ✓ Başarılı (Shopier)
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: OBSERVABILITY */}
      {activeTab === 'observability' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl bg-slate-900/90 border border-slate-800">
            <div>
              <h3 className="text-sm font-bold text-white">Sistem Sağlığı & Canlı İzleme (Observability)</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Backend servisleri, veritabanı senkronizasyonu, ödeme ağ geçidi ve kimlik doğrulama katmanlarının anlık durumu.
              </p>
            </div>
            <button
              onClick={fetchLiveHealth}
              disabled={loadingHealth}
              className="px-3.5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-2 shadow-sm shrink-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingHealth ? 'animate-spin' : ''}`} />
              Şimdi Yeniden Kontrol Et
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {((liveHealth?.services as any[]) || healthMetrics).map((svc: any, idx: number) => {
              const name = svc.name || svc.service || `Servis ${idx + 1}`;
              const isHealthy = (svc.status || '').toLowerCase().includes('healthy') || svc.status === 'SAĞLIKLI';
              const details = svc.details || svc.message || '';
              return (
                <div key={name} className="p-5 rounded-xl bg-slate-900/90 border border-slate-800 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-slate-200">{name}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 ${
                        isHealthy ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/60' : 'bg-amber-950/80 text-amber-300 border border-amber-800/60'
                      }`}
                    >
                      {isHealthy ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <AlertTriangle className="w-3 h-3 text-amber-400" />}
                      {isHealthy ? 'SAĞLIKLI' : svc.status || 'DEGRADED'}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-slate-300">
                      {isHealthy ? 'Normal Çalışıyor' : 'Dikkat Gerektirir'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">{details}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB 5: BACKGROUND JOBS */}
      {activeTab === 'jobs' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Asenkron Görev Kuyruğu (Background Queue)</h3>
              <p className="text-xs text-slate-500">Domain doğrulama, SSL yenileme, önbellek temizleme ve yedekleme görevleri.</p>
            </div>
            <button
              onClick={handleTriggerJobs}
              disabled={isProcessingJobs}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-2 shadow"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isProcessingJobs ? 'animate-spin' : ''}`} />
              Kuyruğu Şimdi Tetikle
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3.5 px-4">Görev ID</th>
                  <th className="py-3.5 px-4">Tür</th>
                  <th className="py-3.5 px-4">Durum</th>
                  <th className="py-3.5 px-4">Deneme</th>
                  <th className="py-3.5 px-4">Tarih</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-400">
                      Bekleyen veya geçmiş arka plan görevi bulunmuyor.
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                    <tr key={job.jobId} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-mono font-bold text-slate-900">{job.jobId}</td>
                      <td className="py-3 px-4 font-semibold text-slate-700">{job.type}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            job.status === 'COMPLETED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : job.status === 'FAILED'
                              ? 'bg-rose-100 text-rose-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {job.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 font-mono">
                        {job.attempts} / {job.maxAttempts}
                      </td>
                      <td className="py-3 px-4 text-slate-400 font-mono text-[11px]">
                        {new Date(job.createdAt).toLocaleTimeString('tr-TR')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: TENANTS */}
      {activeTab === 'tenants' && <AdminTenantsView />}

      {/* TAB: ORDERS */}
      {activeTab === 'orders' && <AdminOrdersView />}

      {/* TAB: UPCOMING PAYMENTS */}
      {activeTab === 'upcoming' && <AdminUpcomingPaymentsView />}

      {/* TAB: DOMAINS */}
      {activeTab === 'domains' && <AdminDomainsView />}

      {/* TAB: MESSAGES */}
      {activeTab === 'messages' && <AdminMessagesView />}

      {/* TAB: TRIALS */}
      {activeTab === 'trials' && <AdminTrialsView />}

      {/* TAB: SETTINGS */}
      {activeTab === 'settings' && <AdminPlatformSettingsView />}

      {/* TAB: AUDIT LOGS */}
      {activeTab === 'logs' && <AdminAuditLogsView />}

      {/* TAB 6: SERVICE REQUESTS */}
      {activeTab === 'services' && <AdminServiceRequestsView />}

      {/* TAB 7: PRICING & PLANS */}
      {activeTab === 'pricing' && <AdminPricingPlansView />}

      {/* TAB 8: PORTFOLIO & REFERENCES */}
      {activeTab === 'portfolio' && <AdminPortfolioManager />}

      {/* BOTTOM SECTION: SUPER ADMIN PORTFOLIO & REFERENCE STATUS OVERVIEW */}
      {activeTab !== 'portfolio' && (
        <div className="mt-10 p-6 rounded-3xl bg-white border border-slate-200 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-600/20 shrink-0">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-extrabold text-slate-900 text-sm sm:text-base">
                  Adiosoft Tarafından Gerçekleştirilen Projeler & Referanslar
                </h4>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Resmi Veritabanı
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Pazarlama web sitemizde ve kurumsal vitrinde sergilenen gerçek müşteri projelerini ve mobil yazılımları yönetin.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setActiveTab('portfolio')}
            className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-xs transition flex items-center gap-2 whitespace-nowrap shrink-0"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>Referansları Yönet</span>
          </button>
        </div>
      )}

      {selectedUserForDetail && (
        <UserDetailModal
          user={selectedUserForDetail}
          sites={sites}
          orders={orders}
          transactions={transactions}
          onClose={() => setSelectedUserForDetail(null)}
          onUserUpdated={() => {
            loadBillingData();
          }}
        />
      )}

      {exportModalSite && (
        <WebsiteExportModal
          site={exportModalSite}
          isOpen={!!exportModalSite}
          onClose={() => setExportModalSite(null)}
        />
      )}
    </div>
  );
};
